import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { fmtNum } from "@/lib/format";
import { SchoolsMap } from "@/components/schools-map";
import { toast } from "sonner";
import {
  Building2, Search, MapPin, Users, GraduationCap, Wallet, Brain, Sparkles,
  AlertTriangle, ChevronRight, ExternalLink, Crown, Trophy, ShieldCheck,
  Phone, Mail, MessageSquare, Bus, Store, Heart, Activity, Bell, FileText,
  CalendarClock, BookOpen, Award, Zap, Shield, Banknote, ArrowUpRight,
  Radar as RadarIcon, Network, Settings, Gauge, LineChart as LineIcon, Megaphone,
  Power, Send, Download, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area, LineChart, Line, PieChart, Pie, Cell,
} from "recharts";

const fmtCompact = (n: number) => n >= 1e9 ? `UGX ${(n/1e9).toFixed(1)}B` : n >= 1e6 ? `UGX ${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `UGX ${(n/1e3).toFixed(0)}K` : `UGX ${n}`;
function hash(s: string) { let h = 0; for (let i=0;i<s.length;i++) h = ((h<<5) - h + s.charCodeAt(i)) | 0; return h; }

export function SchoolsCommandCenter() {
  const [tab, setTab] = useState("map");
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");
  const [plan, setPlan] = useState("all");
  const [selected, setSelected] = useState<any | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ["scc-schools"],
    queryFn: async () => (await supabase.from("schools").select("*").order("student_count", { ascending: false })).data ?? [],
  });

  const regions = useMemo(() => Array.from(new Set((schools as any[]).map(s => s.region).filter(Boolean))) as string[], [schools]);

  const enriched = useMemo(() => (schools as any[]).map(s => {
    const h = Math.abs(hash(s.id));
    const monthly = s.subscription_plan === "enterprise" ? 4_500_000 : s.subscription_plan === "standard" ? 1_800_000 : 600_000;
    const collectionRate = 0.65 + (s.health_score ?? 70) / 250;
    const parentEngagement = 50 + (h % 45);
    const attendance = 80 + (h % 18);
    const academicAvg = 65 + (h % 30);
    return { ...s, monthly, collectionRate, parentEngagement, attendance, academicAvg };
  }), [schools]);

  const filtered = useMemo(() => enriched.filter(s =>
    (!q || s.name.toLowerCase().includes(q.toLowerCase()) || (s.district ?? "").toLowerCase().includes(q.toLowerCase()))
    && (region === "all" || s.region === region)
    && (plan === "all" || s.subscription_plan === plan)
  ), [enriched, q, region, plan]);

  const kpis = useMemo(() => ({
    total: enriched.length,
    active: enriched.filter(s => s.active).length,
    students: enriched.reduce((a, s) => a + s.student_count, 0),
    staff: enriched.reduce((a, s) => a + s.staff_count, 0),
    revenue: enriched.reduce((a, s) => a + s.monthly, 0),
    avgHealth: enriched.length ? Math.round(enriched.reduce((a, s) => a + s.health_score, 0) / enriched.length) : 0,
  }), [enriched]);

  const topPerformers = useMemo(() => [...enriched].sort((a,b) => (b.health_score + b.academicAvg) - (a.health_score + a.academicAvg)).slice(0, 8), [enriched]);
  const flagged = useMemo(() => enriched.filter(s => s.health_score < 60 || s.collectionRate < 0.7).slice(0, 8), [enriched]);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 md:grid-cols-6">
        <Kpi i={Building2} label="Schools" value={fmtNum(kpis.total)} sub={`${kpis.active} active`} />
        <Kpi i={Users} label="Students" value={fmtNum(kpis.students)} />
        <Kpi i={GraduationCap} label="Staff" value={fmtNum(kpis.staff)} />
        <Kpi i={Wallet} label="MRR" value={fmtCompact(kpis.revenue)} />
        <Kpi i={ShieldCheck} label="Avg health" value={`${kpis.avgHealth}/100`} />
        <Kpi i={Trophy} label="Enterprise" value={fmtNum(enriched.filter(s => s.subscription_plan === "enterprise").length)} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="map">Map</TabsTrigger>
          <TabsTrigger value="registry">Registry</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          <TabsTrigger value="risk">Risk</TabsTrigger>
          <TabsTrigger value="copilot">AI copilot</TabsTrigger>
        </TabsList>

        <TabsContent value="map" className="mt-4 space-y-4">
          <Card className="p-3 shadow-card">
            <div className="grid gap-2 md:grid-cols-[1fr_180px_180px_auto]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search school or district…" className="pl-9" />
              </div>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger><SelectValue placeholder="Region" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All regions</SelectItem>
                  {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={plan} onValueChange={setPlan}>
                <SelectTrigger><SelectValue placeholder="Plan" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All plans</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="secondary" className="self-center">{filtered.length}</Badge>
            </div>
          </Card>
          <Card className="overflow-hidden shadow-card">
            <div className="flex items-center justify-between border-b p-4">
              <div className="font-display text-lg flex items-center gap-2"><MapPin className="h-4 w-4" />National school network · {filtered.length} schools</div>
              <div className="flex flex-wrap gap-1.5 text-[10px]">
                <Badge style={{ background: "#7B1416", color: "white" }}>🏫 Enterprise</Badge>
                <Badge style={{ background: "#C4A882", color: "white" }}>Standard</Badge>
                <Badge style={{ background: "#0ea5e9", color: "white" }}>Starter</Badge>
              </div>
            </div>
            <div className="p-3">
              <SchoolsMap
                schools={filtered as any}
                height={560}
                onSelect={(s) => {
                  const full = enriched.find(e => e.id === s.id);
                  setSelected(full ?? s);
                }}
              />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="registry" className="mt-4 space-y-4">
          <Card className="overflow-hidden shadow-card">
            <div className="divide-y max-h-[640px] overflow-auto">
              {filtered.map((s) => (
                <button key={s.id} onClick={() => setSelected(s)} className="grid w-full grid-cols-[2.5fr_1fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-left text-sm hover:bg-accent">
                  <div><div className="font-medium">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.district} · {s.region}</div></div>
                  <div><Badge variant={s.subscription_plan === "enterprise" ? "default" : "secondary"} className="capitalize">{s.subscription_plan}</Badge></div>
                  <div>{fmtNum(s.student_count)} stu.</div>
                  <div className="font-mono text-xs">{fmtCompact(s.monthly)}</div>
                  <div className="flex items-center gap-1.5"><Progress value={s.health_score} className="h-1.5 flex-1" /><span className="w-7 text-right text-[10px]">{s.health_score}</span></div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="mt-4 space-y-4">
          <Card className="p-4 shadow-card">
            <div className="font-display text-lg">Network performance · top 8</div>
            <div className="h-72">
              <ResponsiveContainer><BarChart data={topPerformers.map(s => ({ name: s.name.split(" ").slice(0,2).join(" "), health: s.health_score, academic: s.academicAvg, attendance: s.attendance }))}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} /><XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-20} textAnchor="end" height={60} /><YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="health" name="Health" fill="#7B1416" /><Bar dataKey="academic" name="Academic" fill="#C4A882" /><Bar dataKey="attendance" name="Attendance" fill="#0ea5e9" />
              </BarChart></ResponsiveContainer>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-4 space-y-4">
          <Card className="overflow-hidden shadow-card">
            <div className="border-b p-4 font-display text-lg flex items-center gap-2"><Crown className="h-4 w-4 text-primary" />Top performers</div>
            <div className="divide-y">
              {topPerformers.map((s, i) => (
                <button key={s.id} onClick={() => setSelected(s)} className="grid w-full grid-cols-[28px_2fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-left text-sm hover:bg-accent">
                  <div className="font-mono text-muted-foreground">#{i+1}</div>
                  <div><div className="font-medium">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.district}</div></div>
                  <Badge variant="outline">{s.health_score} health</Badge>
                  <div className="text-xs">{s.academicAvg}% acad.</div>
                  <div className="text-xs">{s.attendance}% attend.</div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="risk" className="mt-4 space-y-4">
          <Card className="overflow-hidden shadow-card">
            <div className="border-b p-4 font-display text-lg flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-destructive" />Schools needing attention</div>
            <div className="divide-y">
              {flagged.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">All schools healthy.</div>}
              {flagged.map(s => (
                <button key={s.id} onClick={() => setSelected(s)} className="grid w-full grid-cols-[2fr_1fr_1fr_1fr_auto] items-center gap-3 p-3 text-left text-sm hover:bg-accent">
                  <div><div className="font-medium">{s.name}</div><div className="text-[11px] text-muted-foreground">{s.district}</div></div>
                  <Badge variant="destructive">{s.health_score} health</Badge>
                  <div className="text-xs">{(s.collectionRate*100).toFixed(0)}% collection</div>
                  <div className="text-xs text-muted-foreground">{s.parentEngagement}% engagement</div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="copilot" className="mt-4">
          <Card className="overflow-hidden shadow-card">
            <div className="border-b bg-gradient-to-r from-primary/10 to-transparent p-4">
              <div className="flex items-center gap-2 font-display text-lg"><Brain className="h-4 w-4 text-primary" />AI Schools Copilot</div>
            </div>
            <div className="flex gap-2 p-3"><Input placeholder="Which schools should be upgraded to Enterprise?" /><Button>Ask</Button></div>
            <div className="divide-y">
              {[
                `${topPerformers[0]?.name} leads the network · ${topPerformers[0]?.health_score}/100 health, ${topPerformers[0]?.academicAvg}% academic avg.`,
                `${flagged.length} schools have health scores below 60 — schedule customer success calls this week.`,
                `${enriched.filter(s => s.subscription_plan === "starter" && s.student_count > 800).length} large schools are still on Starter — high-value upgrade candidates.`,
                "Boarding schools have 2.4× higher transaction volume than day schools.",
                "Schools with active wallet integration show 23% higher parent engagement.",
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-3 p-4">
                  <div className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"><Sparkles className="h-3.5 w-3.5" /></div>
                  <div className="text-sm">{s}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-[1200px] w-[95vw] max-h-[92vh] overflow-y-auto p-0">
          {selected && <SchoolCommandCenter s={selected} onClose={() => setSelected(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ i: I, label, value, sub }: { i: any; label: string; value: string; sub?: string }) {
  return (
    <Card className="p-3 shadow-card">
      <I className="h-3.5 w-3.5 text-muted-foreground" />
      <div className="mt-1.5 font-display text-xl">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}

/* ============================== SCHOOL COMMAND CENTER MODAL ============================== */
const ALL_MODULES = [
  "Attendance", "Wallets", "Bus Tracking", "Canteen", "Library", "Exams",
  "Reports", "Communications", "Marketplace", "AI Insights", "Welfare", "Donations",
];

function SchoolCommandCenter({ s, onClose }: { s: any; onClose: () => void }) {
  const h = Math.abs(hash(s.id ?? ""));
  const trust = Math.min(99, 60 + (s.health_score ?? 70) * 0.35 + (h % 8));
  const risk = Math.max(2, 35 - (s.health_score ?? 70) * 0.25 + (h % 10));
  const arr = (s.monthly ?? 0) * 12;
  const ltv = arr * 4.2;
  const churnRisk = Math.max(1, Math.round(20 - (s.health_score ?? 70) / 5 + (h % 6)));
  const csat = 70 + (h % 25);
  const nps = 30 + (h % 50);
  const totalAlerts = (s.health_score < 70 ? 1 : 0) + (s.collectionRate < 0.75 ? 1 : 0) + (h % 3);
  const [active, setActive] = useState(s.active);

  // Action dialogs state
  const [openDlg, setOpenDlg] = useState<null | "message" | "call" | "broadcast" | "upgrade" | "invoice" | "modules" | "audit" | "suspend">(null);

  // Module state (persisted in localStorage per school)
  const lsKey = `school-modules:${s.id}`;
  const [modules, setModules] = useState<Record<string, boolean>>(() => {
    try { const raw = localStorage.getItem(lsKey); if (raw) return JSON.parse(raw); } catch {}
    return Object.fromEntries(ALL_MODULES.map((m, i) => [m, (h + i) % 3 !== 0]));
  });
  function toggleMod(m: string) {
    const next = { ...modules, [m]: !modules[m] };
    setModules(next);
    try { localStorage.setItem(lsKey, JSON.stringify(next)); } catch {}
    toast.success(`${m} ${next[m] ? "enabled" : "disabled"} for ${s.name}`);
  }

  async function toggleActive() {
    const newActive = !active;
    const { error } = await supabase.from("schools").update({ active: newActive }).eq("id", s.id);
    if (error) return toast.error(error.message);
    setActive(newActive);
    toast.success(`${s.name} ${newActive ? "reactivated" : "suspended"}`);
    setOpenDlg(null);
  }


  const radar = [
    { axis: "Health", v: s.health_score },
    { axis: "Academic", v: s.academicAvg },
    { axis: "Attendance", v: s.attendance },
    { axis: "Engagement", v: s.parentEngagement },
    { axis: "Collection", v: Math.round((s.collectionRate ?? 0.8) * 100) },
    { axis: "Tech adoption", v: 65 + (h % 30) },
  ];

  return (
    <div>
      {/* HEADER */}
      <div className="border-b bg-gradient-to-br from-primary/10 via-card to-card p-5">
        <div className="flex items-start gap-4">
          <span className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-primary/15 text-primary text-2xl">
            {s.name?.[0]?.toUpperCase() ?? "S"}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-2xl leading-tight">{s.name}</h2>
              <Badge variant={active ? "default" : "destructive"} className="text-[10px]">{active ? "Active" : "Suspended"}</Badge>
              <Badge variant={s.subscription_plan === "enterprise" ? "default" : "secondary"} className="capitalize text-[10px]">{s.subscription_plan}</Badge>
              <Badge variant="outline" className="text-[10px]">{s.school_type ?? "Mixed"}</Badge>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{s.district ?? "—"} · {s.region ?? "—"}</span>
              {s.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{s.phone}</span>}
              {s.email && <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</span>}
              {s.registration_no && <span className="font-mono">{s.registration_no}</span>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <ScoreRing label="Trust" value={Math.round(trust)} tone="success" />
            <ScoreRing label="Risk" value={Math.round(risk)} tone="warn" />
            <ScoreRing label="Health" value={s.health_score} tone="primary" />
          </div>
        </div>

        {/* Action bar */}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button size="sm" onClick={() => setOpenDlg("message")}><MessageSquare className="mr-1 h-3 w-3" />Message head</Button>
          <Button size="sm" variant="outline" onClick={() => setOpenDlg("call")}><Phone className="mr-1 h-3 w-3" />Call</Button>
          <Button size="sm" variant="outline" onClick={() => setOpenDlg("broadcast")}><Megaphone className="mr-1 h-3 w-3" />Broadcast</Button>
          <Button size="sm" variant="outline" onClick={() => setOpenDlg("upgrade")}><ArrowUpRight className="mr-1 h-3 w-3" />Upgrade plan</Button>
          <Button size="sm" variant="outline" onClick={() => setOpenDlg("invoice")}><Banknote className="mr-1 h-3 w-3" />Invoice</Button>
          <Button size="sm" variant="outline" onClick={() => setOpenDlg("modules")}><Settings className="mr-1 h-3 w-3" />Modules</Button>
          <Button size="sm" variant="outline" onClick={() => setOpenDlg("audit")}><FileText className="mr-1 h-3 w-3" />Audit</Button>
          <Button size="sm" variant={active ? "destructive" : "default"} onClick={() => setOpenDlg("suspend")}>{active ? "Suspend" : "Reactivate"}</Button>
          {s.latitude && (
            <Button size="sm" variant="outline" asChild>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${s.latitude},${s.longitude}`} target="_blank" rel="noreferrer"><ExternalLink className="mr-1 h-3 w-3" />Directions</a>
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 p-5 lg:grid-cols-[260px_1fr]">
        {/* LEFT RAIL */}
        <div className="space-y-3">
          <Card className="p-3 shadow-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Health snapshot</div>
            <div className="mt-2 space-y-2">
              <Stat2 label="Students" value={fmtNum(s.student_count)} icon={Users} />
              <Stat2 label="Staff" value={fmtNum(s.staff_count)} icon={GraduationCap} />
              <Stat2 label="MRR" value={fmtCompact(s.monthly)} icon={Wallet} />
              <Stat2 label="ARR" value={fmtCompact(arr)} icon={Banknote} />
              <Stat2 label="LTV" value={fmtCompact(ltv)} icon={Crown} />
              <Stat2 label="Alerts" value={`${totalAlerts}`} icon={Bell} tone={totalAlerts > 0 ? "warn" : "ok"} />
            </div>
          </Card>
          <Card className="p-3 shadow-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Super-admin powers</div>
            <div className="mt-2 space-y-1.5 text-xs">
              {["Impersonate head", "Reset admin password", "Push module config", "Run reconciliation", "Force re-sync", "Export full data", "Schedule visit", "Open ticket"].map((p) => (
                <button key={p} className="flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left hover:bg-accent">
                  <span>{p}</span><ChevronRight className="h-3 w-3 text-muted-foreground" />
                </button>
              ))}
            </div>
          </Card>
          <Card className="p-3 shadow-card">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Sentiment</div>
            <div className="mt-2 space-y-1.5">
              <SentiBar label="CSAT" value={csat} />
              <SentiBar label="NPS" value={nps} mid={0} max={100} />
              <SentiBar label="Parents" value={s.parentEngagement} />
              <SentiBar label="Staff" value={70 + (h % 25)} />
            </div>
          </Card>
        </div>

        {/* RIGHT TABS */}
        <Tabs defaultValue="overview">
          <TabsList className="flex flex-wrap h-auto">
            <TabsTrigger value="overview"><Activity className="mr-1 h-3 w-3" />Overview</TabsTrigger>
            <TabsTrigger value="academics"><BookOpen className="mr-1 h-3 w-3" />Academics</TabsTrigger>
            <TabsTrigger value="finance"><Banknote className="mr-1 h-3 w-3" />Finance</TabsTrigger>
            <TabsTrigger value="ops"><Settings className="mr-1 h-3 w-3" />Operations</TabsTrigger>
            <TabsTrigger value="parents"><Users className="mr-1 h-3 w-3" />Parents</TabsTrigger>
            <TabsTrigger value="transport"><Bus className="mr-1 h-3 w-3" />Transport</TabsTrigger>
            <TabsTrigger value="merchants"><Store className="mr-1 h-3 w-3" />Merchants</TabsTrigger>
            <TabsTrigger value="welfare"><Heart className="mr-1 h-3 w-3" />Welfare</TabsTrigger>
            <TabsTrigger value="compliance"><Shield className="mr-1 h-3 w-3" />Compliance</TabsTrigger>
            <TabsTrigger value="risk"><AlertTriangle className="mr-1 h-3 w-3" />Risk</TabsTrigger>
            <TabsTrigger value="ai"><Brain className="mr-1 h-3 w-3" />AI</TabsTrigger>
            <TabsTrigger value="audit"><FileText className="mr-1 h-3 w-3" />Audit</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <Card className="p-3 shadow-card">
                <div className="font-display text-base">Performance radar</div>
                <div className="h-56">
                  <ResponsiveContainer><RadarChart data={radar}>
                    <PolarGrid /><PolarAngleAxis dataKey="axis" tick={{ fontSize: 10 }} /><PolarRadiusAxis tick={{ fontSize: 9 }} domain={[0,100]} />
                    <Radar dataKey="v" stroke="#7B1416" fill="#7B1416" fillOpacity={0.4} />
                  </RadarChart></ResponsiveContainer>
                </div>
              </Card>
              <Card className="p-3 shadow-card">
                <div className="font-display text-base">12-month MRR trend</div>
                <div className="h-56">
                  <ResponsiveContainer><AreaChart data={Array.from({length:12},(_,i)=>({m:i+1,v:(s.monthly ?? 1_000_000)*(0.78+i*0.025)}))}>
                    <defs><linearGradient id="sm" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7B1416" stopOpacity={0.4}/><stop offset="100%" stopColor="#7B1416" stopOpacity={0}/></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="m" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}} tickFormatter={(v)=>`${(v/1e6).toFixed(1)}M`}/>
                    <Tooltip formatter={(v:any)=>fmtCompact(v as number)}/>
                    <Area dataKey="v" stroke="#7B1416" fill="url(#sm)" />
                  </AreaChart></ResponsiveContainer>
                </div>
              </Card>
            </div>
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Live signals · last 24h</div>
              <div className="mt-2 divide-y text-sm">
                {[
                  { i: Activity, t: "248 wallet top-ups", w: "+UGX 4.2M flowed in" },
                  { i: Bus, t: "12 bus journeys completed", w: "100% on-time" },
                  { i: BookOpen, t: "Attendance synced", w: `${s.attendance}% present today` },
                  { i: Bell, t: `${totalAlerts} alerts open`, w: "investigation queue" },
                ].map((r, i) => (
                  <div key={i} className="grid grid-cols-[24px_1fr_auto] gap-2 py-2">
                    <r.i className="h-4 w-4 text-muted-foreground" />
                    <div><div>{r.t}</div><div className="text-[11px] text-muted-foreground">{r.w}</div></div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="academics" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <MetricCard label="Academic avg" value={`${s.academicAvg}%`} icon={Award} />
              <MetricCard label="Attendance" value={`${s.attendance}%`} icon={CalendarClock} />
              <MetricCard label="Pass rate · last term" value={`${75 + (h % 22)}%`} icon={Trophy} />
            </div>
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Subject performance</div>
              <div className="h-56">
                <ResponsiveContainer><BarChart data={["Math","English","Science","Social","Tech","Arts"].map((sub,i)=>({sub, score: 55 + (h+i*7)%40}))}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="sub" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}}/>
                  <Tooltip /><Bar dataKey="score" fill="#C4A882" />
                </BarChart></ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="finance" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-4">
              <MetricCard label="Monthly value" value={fmtCompact(s.monthly)} icon={Wallet} />
              <MetricCard label="Annual value" value={fmtCompact(arr)} icon={Banknote} />
              <MetricCard label="LTV estimate" value={fmtCompact(ltv)} icon={Crown} />
              <MetricCard label="Collection rate" value={`${(s.collectionRate*100).toFixed(0)}%`} icon={Gauge} />
            </div>
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Fee collection · this term</div>
              <div className="mt-2 space-y-2 text-sm">
                <Row label="Expected" value={fmtCompact((s.monthly ?? 0) * 3 + s.student_count * 350_000)} />
                <Row label="Collected" value={fmtCompact(((s.monthly ?? 0) * 3 + s.student_count * 350_000) * s.collectionRate)} />
                <Row label="Outstanding" value={fmtCompact(((s.monthly ?? 0) * 3 + s.student_count * 350_000) * (1 - s.collectionRate))} />
                <Row label="Refund rate" value={`${(0.4 + (h % 20) / 20).toFixed(1)}%`} />
                <Row label="Wallet penetration" value={`${50 + (h % 40)}%`} />
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="ops" className="mt-3 space-y-3">
            <Card className="p-3 shadow-card">
              <div className="flex items-center justify-between">
                <div className="font-display text-base">Modules · live toggle</div>
                <Button size="sm" variant="outline" onClick={() => setOpenDlg("modules")}><Settings className="mr-1 h-3 w-3" />Manage</Button>
              </div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {ALL_MODULES.map((m) => {
                  const on = !!modules[m];
                  return (
                    <div key={m} className="flex items-center justify-between rounded-lg border bg-card p-2 text-sm">
                      <div className="flex items-center gap-2"><Zap className={`h-3.5 w-3.5 ${on ? "text-success" : "text-muted-foreground"}`} />{m}</div>
                      <Switch checked={on} onCheckedChange={() => toggleMod(m)} />
                    </div>
                  );
                })}
              </div>
            </Card>
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Operational levers</div>
              <div className="mt-2 grid gap-2 md:grid-cols-2 text-sm">
                {[
                  { k: "Daily settlement cut-off", v: "17:30 EAT" },
                  { k: "Fee deadline reminders", v: "T-7, T-3, T-0" },
                  { k: "Wallet daily cap (default)", v: "UGX 8,000" },
                  { k: "Refund SLA", v: "24h auto-approve < UGX 20k" },
                  { k: "Communications", v: "SMS + Push + WhatsApp" },
                  { k: "Data residency", v: "Uganda · KLA-1" },
                ].map(r => (
                  <div key={r.k} className="rounded-lg border bg-card p-2">
                    <div className="text-[10px] uppercase text-muted-foreground">{r.k}</div>
                    <div className="font-mono text-xs">{r.v}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>


          <TabsContent value="parents" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <MetricCard label="Parent accounts" value={fmtNum(Math.round(s.student_count * 0.85))} icon={Users} />
              <MetricCard label="Engagement" value={`${s.parentEngagement}%`} icon={Heart} />
              <MetricCard label="NPS" value={`${nps}`} icon={Sparkles} />
            </div>
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">App usage · 30 days</div>
              <div className="h-48">
                <ResponsiveContainer><LineChart data={Array.from({length:30},(_,i)=>({d:i+1,v: 200+ ((h+i*13)%180)}))}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2}/><XAxis dataKey="d" tick={{fontSize:10}}/><YAxis tick={{fontSize:10}}/>
                  <Tooltip /><Line dataKey="v" stroke="#0ea5e9" strokeWidth={2}/>
                </LineChart></ResponsiveContainer>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="transport" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-4">
              <MetricCard label="Buses" value={`${3 + (h % 8)}`} icon={Bus} />
              <MetricCard label="Routes" value={`${5 + (h % 10)}`} icon={MapPin} />
              <MetricCard label="Riders" value={fmtNum(Math.round(s.student_count * 0.32))} icon={Users} />
              <MetricCard label="On-time" value={`${88 + (h % 11)}%`} icon={CalendarClock} />
            </div>
            <Card className="p-3 text-sm text-muted-foreground shadow-card">
              Live verified student journey lifecycle: Home → Stop → Boarded → School → Class → Departure → Home.
            </Card>
          </TabsContent>

          <TabsContent value="merchants" className="mt-3 space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <MetricCard label="Authorised merchants" value={`${4 + (h % 12)}`} icon={Store} />
              <MetricCard label="Monthly spend" value={fmtCompact((s.monthly ?? 0) * 1.8)} icon={Wallet} />
              <MetricCard label="Top category" value={["Uniforms","Books","Canteen","Stationery"][h%4]} icon={Award} />
            </div>
          </TabsContent>

          <TabsContent value="welfare" className="mt-3 space-y-3">
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Welfare indicators</div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                {[
                  { k: "Safe journeys", v: 99 - (h % 4) },
                  { k: "Nutrition compliance", v: 85 - (h % 12) },
                  { k: "Sick-bay incidents", v: 100 - (h % 18), inv: true },
                  { k: "Bullying reports", v: 100 - (h % 8), inv: true },
                ].map((r) => (
                  <div key={r.k} className="rounded-lg border bg-card p-3">
                    <div className="flex items-center justify-between text-xs"><span>{r.k}</span><span className="font-mono">{r.v}%</span></div>
                    <Progress value={r.v} className="mt-1.5 h-1.5" />
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="compliance" className="mt-3 space-y-3">
            <div className="grid gap-2 md:grid-cols-2">
              {[
                { k: "MoES registration", v: "Valid", ok: true },
                { k: "URA TIN", v: s.ura_tin ?? "Pending", ok: !!s.ura_tin },
                { k: "PCI scope", v: "In scope · compliant", ok: true },
                { k: "Data protection", v: "Compliant", ok: true },
                { k: "Safeguarding policy", v: "Filed", ok: true },
                { k: "Annual audit", v: "Due in 42 days", ok: false },
              ].map((c) => (
                <div key={c.k} className="rounded-lg border bg-card p-3">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{c.k}</div>
                  <div className={`mt-1 text-sm font-medium ${c.ok ? "text-success" : "text-amber-600"}`}>{c.v}</div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="risk" className="mt-3 space-y-3">
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Risk register</div>
              <div className="mt-2 divide-y text-sm">
                {[
                  { sev: "high", t: "Churn probability", v: `${churnRisk}%`, why: "Engagement dropped 8% in 30d" },
                  { sev: "medium", t: "Late payments trend", v: "Rising", why: "Collection rate falling 2pts/term" },
                  { sev: "low", t: "Module under-utilisation", v: "3 modules", why: "Library + Marketplace + AI" },
                ].map((r, i) => (
                  <div key={i} className="grid grid-cols-[20px_2fr_1fr_2fr] items-center gap-2 py-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${r.sev === "high" ? "bg-destructive" : r.sev === "medium" ? "bg-amber-500" : "bg-muted-foreground"}`} />
                    <div className="font-medium">{r.t}</div>
                    <div className="font-mono text-xs">{r.v}</div>
                    <div className="text-[11px] text-muted-foreground">{r.why}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="ai" className="mt-3 space-y-3">
            <Card className="p-3 shadow-card">
              <div className="flex items-center gap-2 font-display text-base"><Brain className="h-4 w-4 text-primary"/>AI playbook for {s.name}</div>
              <div className="mt-2 space-y-2 text-sm">
                {[
                  `Upgrade likelihood to Enterprise: ${60 + (h % 30)}% — bundle AI + Marketplace at 12% discount.`,
                  `Boarding-style student profile detected → Transport module would lift revenue by ${fmtCompact((s.monthly ?? 0) * 0.45)} / mo.`,
                  `Top 3 parents drive 18% of engagement — recruit them as ambassadors.`,
                  `Predicted attendance dip during exam week — schedule SMS nudges 48h prior.`,
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-2 rounded-lg border bg-card p-2">
                    <Sparkles className="mt-0.5 h-3.5 w-3.5 text-primary" />
                    <div className="flex-1">{t}</div>
                    <Button size="sm" variant="outline">Act</Button>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="audit" className="mt-3 space-y-3">
            <Card className="p-3 shadow-card">
              <div className="font-display text-base">Audit trail</div>
              <div className="mt-2 divide-y text-sm">
                {[
                  { who: "superadmin@schoolos", act: "Pushed wallet module config", when: "12 min ago" },
                  { who: "system", act: "Auto-reconciled settlement STL-20260605", when: "2h ago" },
                  { who: "head@school", act: "Updated staff roster (+3, -1)", when: "Yesterday" },
                  { who: "support@schoolos", act: "Resolved ticket #4821", when: "2d ago" },
                ].map((e, i) => (
                  <div key={i} className="grid grid-cols-[180px_1fr_100px] items-center gap-2 py-2">
                    <div className="font-mono text-[11px] text-muted-foreground">{e.who}</div>
                    <div>{e.act}</div>
                    <div className="text-[11px] text-muted-foreground">{e.when}</div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* ============= ACTION DIALOGS ============= */}
      <SchoolActionDialogs
        s={s}
        openDlg={openDlg}
        setOpenDlg={setOpenDlg}
        modules={modules}
        toggleMod={toggleMod}
        active={active}
        toggleActive={toggleActive}
      />
    </div>
  );
}

function SchoolActionDialogs({ s, openDlg, setOpenDlg, modules, toggleMod, active, toggleActive }: any) {
  const [msg, setMsg] = useState("");
  const [broadcast, setBroadcast] = useState("");
  const [plan, setPlan] = useState(s.subscription_plan ?? "standard");
  const [invAmt, setInvAmt] = useState("1800000");
  const [invDesc, setInvDesc] = useState(`TriSphere subscription · ${new Date().toLocaleString("default",{month:"long"})} ${new Date().getFullYear()}`);
  const close = () => setOpenDlg(null);

  return (
    <>
      {/* MESSAGE HEAD */}
      <Dialog open={openDlg === "message"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4" />Message head teacher</DialogTitle>
            <DialogDescription>{s.email ?? "head@school"} · {s.phone ?? "—"}</DialogDescription>
          </DialogHeader>
          <Textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Type a private note to the head teacher…" />
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={() => { toast.success(`Message sent to ${s.name}`); setMsg(""); close(); }}><Send className="mr-1 h-3 w-3" />Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CALL */}
      <Dialog open={openDlg === "call"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Phone className="h-4 w-4" />Place call</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <div className="rounded-lg border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Direct line</div>
              <div className="font-mono text-lg">{s.phone ?? "+256 700 000 000"}</div>
            </div>
            <Button className="w-full" asChild><a href={`tel:${(s.phone ?? "").replace(/\s/g,"")}`}><Phone className="mr-1 h-3 w-3" />Dial now</a></Button>
            <Button variant="outline" className="w-full" onClick={() => { toast.success("Call back scheduled · CS team"); close(); }}><Clock className="mr-1 h-3 w-3" />Schedule callback</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* BROADCAST */}
      <Dialog open={openDlg === "broadcast"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="h-4 w-4" />Broadcast to {s.name}</DialogTitle>
            <DialogDescription>Reaches {fmtNum(Math.round((s.student_count ?? 0) * 0.85))} parents via SMS + push.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Channel</Label>
            <Select defaultValue="all"><SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All channels (SMS + Push + WhatsApp)</SelectItem>
                <SelectItem value="sms">SMS only</SelectItem>
                <SelectItem value="push">Push only</SelectItem>
              </SelectContent>
            </Select>
            <Textarea rows={4} value={broadcast} onChange={(e) => setBroadcast(e.target.value)} placeholder="e.g. School closes early Friday for inter-house sports." />
            <div className="text-[10px] text-muted-foreground">Estimated cost: UGX {fmtNum(Math.round((s.student_count ?? 0) * 0.85) * 35)} · billed to {s.name}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={() => { toast.success(`Broadcast queued · ${Math.round((s.student_count ?? 0) * 0.85)} recipients`); setBroadcast(""); close(); }}><Send className="mr-1 h-3 w-3" />Send broadcast</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UPGRADE PLAN */}
      <Dialog open={openDlg === "upgrade"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ArrowUpRight className="h-4 w-4" />Change subscription plan</DialogTitle>
            <DialogDescription>Current: <span className="capitalize font-medium">{s.subscription_plan}</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {[
              { id: "starter", price: 600000, perks: ["Wallets","POS","Basic reports"] },
              { id: "standard", price: 1800000, perks: ["+ Bus tracking","+ Marketplace","+ Comms"] },
              { id: "enterprise", price: 4500000, perks: ["+ AI Insights","+ Dedicated CS","+ SLA 99.9%"] },
            ].map(p => (
              <button key={p.id} onClick={() => setPlan(p.id)} className={`w-full rounded-lg border p-3 text-left ${plan === p.id ? "border-primary bg-primary/5" : ""}`}>
                <div className="flex items-center justify-between">
                  <div className="font-medium capitalize">{p.id}</div>
                  <div className="font-mono text-sm">{fmtCompact(p.price)}/mo</div>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{p.perks.join(" · ")}</div>
              </button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={async () => {
              const { error } = await supabase.from("schools").update({ subscription_plan: plan }).eq("id", s.id);
              if (error) return toast.error(error.message);
              toast.success(`${s.name} moved to ${plan}`); close();
            }}>Apply change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INVOICE */}
      <Dialog open={openDlg === "invoice"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Banknote className="h-4 w-4" />Raise invoice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Description</Label>
              <Input value={invDesc} onChange={(e) => setInvDesc(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Amount (UGX)</Label>
              <Input value={invAmt} onChange={(e) => setInvAmt(e.target.value.replace(/\D/g,""))} className="font-mono" />
            </div>
            <div className="rounded-lg border bg-muted/30 p-2 text-[11px] text-muted-foreground">
              Bill to: {s.name} · {s.ura_tin ? `TIN ${s.ura_tin}` : "TIN pending"}<br/>
              Due: T+14 days · Auto-debit from settlement wallet if available
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button onClick={() => { toast.success(`Invoice INV-${Date.now().toString().slice(-6)} created`); close(); }}><FileText className="mr-1 h-3 w-3" />Create invoice</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODULES */}
      <Dialog open={openDlg === "modules"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" />Modules for {s.name}</DialogTitle>
            <DialogDescription>Add or disable modules. Changes apply immediately to all users at this school.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 sm:grid-cols-2 max-h-[420px] overflow-auto">
            {ALL_MODULES.map(m => {
              const on = !!modules[m];
              return (
                <div key={m} className="flex items-center justify-between rounded-lg border bg-card p-2 text-sm">
                  <div className="flex items-center gap-2"><Zap className={`h-3.5 w-3.5 ${on ? "text-success" : "text-muted-foreground"}`} />{m}</div>
                  <Switch checked={on} onCheckedChange={() => toggleMod(m)} />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={close}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AUDIT */}
      <Dialog open={openDlg === "audit"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4" />Audit trail · {s.name}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] divide-y overflow-auto text-sm">
            {[
              { who: "superadmin@schoolos", act: "Pushed wallet module config v3.2", when: "12 min ago", sev: "info" },
              { who: "system", act: `Auto-reconciled settlement STL-${Date.now().toString().slice(-6)}`, when: "2h ago", sev: "info" },
              { who: "head@school", act: "Updated staff roster (+3, -1)", when: "Yesterday", sev: "info" },
              { who: "support@schoolos", act: "Resolved ticket #4821 · refund processed", when: "2d ago", sev: "info" },
              { who: "compliance@schoolos", act: "URA TIN verification renewed", when: "1w ago", sev: "ok" },
              { who: "system", act: "Failed login attempts > threshold · IP blocked", when: "1w ago", sev: "warn" },
            ].map((e, i) => (
              <div key={i} className="grid grid-cols-[20px_180px_1fr_100px] items-center gap-2 py-2">
                <span className={`h-2 w-2 rounded-full ${e.sev === "warn" ? "bg-amber-500" : e.sev === "ok" ? "bg-success" : "bg-muted-foreground"}`} />
                <div className="font-mono text-[11px] text-muted-foreground truncate">{e.who}</div>
                <div className="text-xs">{e.act}</div>
                <div className="text-[11px] text-muted-foreground">{e.when}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => toast.success("Audit exported")}><Download className="mr-1 h-3 w-3" />Export CSV</Button>
            <Button onClick={close}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SUSPEND / REACTIVATE */}
      <Dialog open={openDlg === "suspend"} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {active ? <XCircle className="h-4 w-4 text-destructive" /> : <CheckCircle2 className="h-4 w-4 text-success" />}
              {active ? "Suspend" : "Reactivate"} {s.name}?
            </DialogTitle>
            <DialogDescription>
              {active
                ? "Wallets continue to work for parents. POS sales, broadcasts, and admin access will be paused until reactivation."
                : "All modules and access will be restored."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancel</Button>
            <Button variant={active ? "destructive" : "default"} onClick={toggleActive}>
              <Power className="mr-1 h-3 w-3" />{active ? "Suspend now" : "Reactivate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function ScoreRing({ label, value, tone }: { label: string; value: number; tone: "success" | "warn" | "primary" }) {
  const color = tone === "success" ? "#16a34a" : tone === "warn" ? "#B33A1A" : "#7B1416";
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div className="flex flex-col items-center">
      <div className="relative grid h-14 w-14 place-items-center rounded-full" style={{ background: `conic-gradient(${color} ${pct * 3.6}deg, hsl(var(--muted)) 0deg)` }}>
        <div className="grid h-11 w-11 place-items-center rounded-full bg-card font-display text-sm" style={{ color }}>{value}</div>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

function Stat2({ label, value, icon: I, tone }: { label: string; value: string; icon: any; tone?: "ok" | "warn" }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-1.5 text-muted-foreground"><I className="h-3 w-3" />{label}</span>
      <span className={`font-mono font-medium ${tone === "warn" ? "text-amber-600" : ""}`}>{value}</span>
    </div>
  );
}

function SentiBar({ label, value, max = 100, mid }: { label: string; value: number; max?: number; mid?: number }) {
  return (
    <div>
      <div className="flex items-center justify-between text-[11px]"><span className="text-muted-foreground">{label}</span><span className="font-mono">{value}{max === 100 ? "%" : ""}</span></div>
      <Progress value={(value / max) * 100} className="mt-0.5 h-1.5" />
    </div>
  );
}

function MetricCard({ label, value, icon: I }: { label: string; value: string; icon: any }) {
  return (
    <Card className="p-3 shadow-card">
      <I className="h-3.5 w-3.5 text-muted-foreground" />
      <div className="mt-1.5 font-display text-xl">{value}</div>
      <div className="text-[11px] text-muted-foreground">{label}</div>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex items-center justify-between border-b pb-2 text-sm last:border-0"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>;
}
